import {
    Icon,
    IconNames,
    TableColumnAlignment,
    TableSortDirection,
} from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { useMemo, useState } from 'react';
import {
    StyledHeader,
    StyledHeaderContainer,
    StyledTotalHeader,
    TableHeaderArrows,
    TableHeaderLabel,
    TableHeaderProps,
    TableTotalLabel,
} from '../StyledComponents/TableHeaderStyledComponents';

const TableHeader: React.FC<TableHeaderProps> = ({
    name,
    field,
    index = 1,
    isSortable = true,
    handleSort,
    textAlignment = TableColumnAlignment.Left,
    isTotal = false,
    renderLabel,
}) => {
    const { theme } = useAppContext();

    const [sortDirection, setSortDirection] = useState<TableSortDirection>(
        TableSortDirection.None,
    );

    const sortArrows = useMemo(() => {
        if (isSortable) {
            let iconName, id;
            switch (sortDirection) {
                case TableSortDirection.Ascending:
                    iconName = IconNames.Arrow_DataAscending;
                    id = 'ascending-sort-icon';
                    break;
                case TableSortDirection.Descending:
                    iconName = IconNames.Arrow_DataDescending;
                    id = 'descending-sort-icon';
                    break;
                case TableSortDirection.None:
                default:
                    iconName = IconNames.Arrow_DataNone;
                    id = 'sort-icon';
                    break;
            }

            return (
                <Icon
                    color={theme.colors.greyscale.gray50}
                    name={iconName}
                    id={id}
                />
            );
        }
    }, [name, isSortable, sortDirection]);

    const handleClick = () => {
        if (isSortable && handleSort && field) {
            let newDirection: number;
            switch (sortDirection) {
                case TableSortDirection.None:
                    newDirection = TableSortDirection.Ascending;
                    break;
                case TableSortDirection.Ascending:
                    newDirection = TableSortDirection.Descending;
                    break;
                case TableSortDirection.Descending:
                    newDirection = TableSortDirection.None;
                    break;
            }

            setSortDirection(newDirection);
            handleSort(field, newDirection);
        }
    };

    if (isTotal) {
        return (
            <StyledTotalHeader index={index}>
                <StyledHeaderContainer
                    textAlignment={textAlignment}
                    onClick={() => handleClick()}
                >
                    <TableTotalLabel>{name}</TableTotalLabel>
                </StyledHeaderContainer>
            </StyledTotalHeader>
        );
    }

    return (
        <StyledHeader
            isSortable={isSortable}
            index={index}
            onClick={() => handleClick()}
        >
            <StyledHeaderContainer textAlignment={textAlignment}>
                {renderLabel ?? (
                    <TableHeaderLabel
                        isSortable={isSortable}
                        textAlignment={textAlignment}
                    >
                        {name}
                    </TableHeaderLabel>
                )}

                {isSortable && (
                    <TableHeaderArrows>{sortArrows}</TableHeaderArrows>
                )}
            </StyledHeaderContainer>
        </StyledHeader>
    );
};
export default TableHeader;
